import * as Yup from "yup";
import { FormGroup } from "../../../components/form/models/models";
export const supportTypeForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "sectionHeader",
      },
      label: "Application Eligibility",
    },
    {
      type: "field",
      key: "isApplicationEligible",
      label: "Is the Application Eligible?",
      fieldType: "radio",
      props: {
        options: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
        ],
      },
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      key: "howWasDisabilityEstablished",
      label: "How was Disability Established?",
      fieldType: "radio",
      props: {
        options: [
          { value: "NDIS Participant", label: "NDIS Participant" },
          { value: "ECIS Eligible", label: "ECIS Eligible" },
          {
            value: "Disability or Developmental delay, supporting documentation provided",
            label: "Disability or Developmental delay, supporting documentation provided",
          },
          {
            value: "Continuing assessment for disability, supporting documentation provided",
            label: "Continuing assessment for disability, supporting documentation provided",
          },
          {
            value: "Disability not established, application assessed as eligible under CMN only",
            label: "Disability not established, application assessed as eligible under CMN only",
          },
        ],
      },
      validation: Yup.string().trim().required("A response is required"),
      hideExpression: (context, values) => values.isApplicationEligible !== "Yes" || context.isComplex,
    },
    {
      type: "formGroup",
      key: "reasonIneligible",
      label: "Reason the Application was Ineligible",
      hideExpression: (context, values) => values.isApplicationEligible !== "No",
      test: [
        "chooseOne",
        "A response is required",
        (value: any) => {
          return value.notEligible || value.disabilityNotEstablished || value.childNotMeetCriteria;
        },
      ],
      props: {
        isRequired: true,
      },
      fields: [
        {
          type: "field",
          key: "notEligible",
          fieldType: "checkbox",
          label: "Not eligible for the program",
        },
        {
          type: "field",
          key: "disabilityNotEstablished",
          fieldType: "checkbox",
          label: "Disability not established",
          hideExpression: (context) => context.isComplex,
        },
        {
          type: "field",
          key: "childNotMeetCriteria",
          fieldType: "checkbox",
          label: "Child does not meet criteria",
        },
        {
          type: "formGroup",
          key: "criteriaNotMeet",
          label: "Indicate which criteria the child does not meet",
          hideExpression: (context, values) => !values.reasonIneligible.childNotMeetCriteria,
          test: [
            "chooseOne",
            "A response is required",
            (value: any) => {
              return value.complexNeedsNotEstablished || value.significantRiskNotEstablished || value.restrictedMovementNotEstablished;
            },
          ],
          props: {
            isRequired: true,
          },
          fields: [
            {
              type: "field",
              key: "complexNeedsNotEstablished",
              fieldType: "checkbox",
              label: "Complex Medical Needs not established",
            },
            {
              type: "field",
              key: "significantRiskNotEstablished",
              fieldType: "checkbox",
              label: "Significant Risk not established",
            },
            {
              type: "field",
              key: "restrictedMovementNotEstablished",
              fieldType: "checkbox",
              label: "Restricted Capacity of Movement not established",
            },
          ],
        },
      ],
    },

    {
      type: "field",
      fieldType: "textarea",
      label: "Additional Explanation",
      key: "additionalExplanation",
      warn: "Provide a short summary explaining why the application was ineligible. This will be provided to the kindergarten.",
      validation: Yup.string().trim().required("A response is required"),
      hideExpression: (context, values) => values.isApplicationEligible === "Yes",
    },

    {
      type: "formGroup",
      label: "Type of Support",
      props: {
        labelCss: "sectionHeader",
      },
      fields: [
        {
          type: "field",
          key: "advisory",
          fieldType: "advisory",
          props: {
            type: "warning",
          },
          label:
            "Select each option (checkbox) where the kindergarten has requested support or support is being recommended and complete the associated fields.",
        },
        {
          type: "formGroup",
          key: "typeOfSupportRequested",
          label: "Type of Support Requested",
          test: [
            "chooseOne",
            "A response is required",
            (value: any) => {
              return value.additionalAssistant || value.specialistConsultancy || value.specialistTraining || value.minorWorks;
            },
          ],
          props: {
            isRequired: true,
          },
          fields: [
            {
              type: "field",
              fieldType: "checkbox",
              key: "additionalAssistant",
              label: "Additional Assistant",
              props: {
                showDisabled: true,
              },
            },
            {
              type: "formGroup",
              key: "isAdditionalAssistant",
              classNameOpt: "childComponent",
              hideExpression: (context, values) => {
                return !values?.typeOfSupportRequested?.additionalAssistant;
              },
              fields: [
                {
                  type: "field",
                  key: "additionalAssistantSupportApproved",
                  fieldType: "radio",
                  label: "Is Support Approved?",
                  props: {
                    options: [
                      {
                        label: "Yes",
                        value: 1,
                      },
                      {
                        label: "No - Application Eligible",
                        value: 2,
                      },
                      {
                        label: "No - Application Not Eligible",
                        value: 3,
                      },
                    ],
                  },
                  validation: Yup.number().required("A response is required"),
                },
                {
                  type: "field",
                  fieldType: "number",
                  key: "fundedServiceHours",
                  label: "Number of Funded Service Hours",
                  props: { isFloat: true },
                  warn: "For most applications, this will be 15 hours. However in pre-prep rollout areas this may be up to 30 hours.",
                  validation: Yup.number()
                    .required("A response is required")
                    .min(0.5, "The number of hours must be at least 0.5")
                    .max(30, "The number of hours must not be more than thirty (30)")
                    .test("one-digit-decimal", "Invalid format entered. Please use numbers with a maximum of one (1) decimal place", (value: any) => {
                      const res = /^\d+(\.\d{1,1})?$/.test(value);
                      return res;
                    }),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested?.isAdditionalAssistant.additionalAssistantSupportApproved != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "number",
                  key: "hoursAllocatedToChild",
                  label: "Number of Hours Allocated to the Child",
                  props: { isFloat: true },
                  validation: Yup.number()
                    .required("A response is required")
                    .min(0.5, "The number of hours must be at least 0.5")
                    .max(30, "The number of hours must not be more than thirty (30)")
                    .test("one-digit-decimal", "Invalid format entered. Please use numbers with a maximum of one (1) decimal place", (value: any) => {
                      return /^\d+(\.\d{1,1})?$/.test(value);
                    }),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested?.isAdditionalAssistant.additionalAssistantSupportApproved != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "number",
                  key: "hoursAllocatedForGroupComplexity",
                  label: "Number of Hours Allocated Because of the Group Complexity/Other Factors",
                  warn: 'The number of hours entered in this field will be used as part of the calculation in the "Total Number of Hours" field. You can enter a negative number in this field.',
                  props: { isFloat: true, allowNegatives: true },
                  validation: Yup.number() //length
                    .required("A response is required")
                    .max(30, "The number of hours must not be more than thirty (30)")
                    .test("one-digit-decimal", "Invalid format entered. Please use numbers with a maximum of one (1) decimal place", (value: any) => {
                      return /^-?\d+(\.\d{1,1})?$/.test(value);
                    }),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested?.isAdditionalAssistant.additionalAssistantSupportApproved != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonForGroupComplexity",
                  label: "Explanation of Group Complexity or Other Factors that were Taken into Account",
                  warn: "This will not be provided to the kindergarten",
                  props: { rows: 5, col: 30 },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested?.isAdditionalAssistant.additionalAssistantSupportApproved != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "number",
                  key: "totalHoursAllocated",
                  label: "Total Number of Hours Allocated",
                  props: { disabled: true },
                  validation: Yup.number()
                    .required("A response is required")
                    .min(0.5, "The number of hours must be at least 0.5")
                    .max(30, "The number of hours must not be more than thirty (30)"),
                  calculatedValue: (context, values) =>
                    CalculateTotalHours(
                      values.typeOfSupportRequested.isAdditionalAssistant?.hoursAllocatedToChild,
                      values.typeOfSupportRequested.isAdditionalAssistant?.hoursAllocatedForGroupComplexity
                    ),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isAdditionalAssistant.additionalAssistantSupportApproved != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonForNotFullAllocation",
                  label: "If Less Than 100% Allocation, Explain Why",
                  warn: "This will be provided to the kindergarten",
                  props: { rows: 5, col: 30 },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return (
                      values?.typeOfSupportRequested.isAdditionalAssistant.additionalAssistantSupportApproved != 1 ||
                      values?.typeOfSupportRequested.isAdditionalAssistant.totalHoursAllocated >=
                        values?.typeOfSupportRequested.isAdditionalAssistant.fundedServiceHours
                    );
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonAdditionalAssistantNotApproved",
                  label: "Reason Additional Assistant was Not Approved",
                  warn: "Provide a reason that the additional support was not approved even though the child is eligible. This will be provided to the kindergarten.",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isAdditionalAssistant.additionalAssistantSupportApproved != 2;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonAdditionalAssistantNotEligible",
                  label: "Reason Additional Assistant was Not Approved",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isAdditionalAssistant?.additionalAssistantSupportApproved != 3;
                  },
                },
              ],
            },
            {
              type: "field",
              fieldType: "checkbox",
              key: "specialistConsultancy",
              label: "Specialist Consultancy",
              props: {
                showDisabled: true,
              },
            },
            {
              type: "formGroup",
              key: "isSpecialistConsultancy",
              classNameOpt: "childComponent",
              hideExpression: (context, values) => {
                return !values?.typeOfSupportRequested.specialistConsultancy;
              },
              fields: [
                {
                  type: "field",
                  fieldType: "radio",
                  key: "specialistConsultancyRequestedOrRecommended",
                  label: "Was Specialist Consultancy Requested or Recommended?",
                  props: {
                    options: [
                      {
                        label: "Requested by the Kindergarten",
                        value: 1,
                      },
                      {
                        label: "Recommended by the KIS Team",
                        value: 2,
                      },
                    ],
                  },
                  validation: Yup.string().trim().required("A response is required"),
                },
                {
                  type: "field",
                  fieldType: "radio",
                  key: "specialistConsultancySupportApproved",
                  label: "Is Support Approved?",
                  props: {
                    options: [
                      {
                        label: "Yes",
                        value: 1,
                      },
                      {
                        label: "No - Application Eligible",
                        value: 2,
                      },
                      {
                        label: "No - Application Not Eligible",
                        value: 3,
                      },
                    ],
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancyRequestedOrRecommended != 1;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "summaryOfSpecialistConsultancyGranted",
                  label: "Summary of the Specialist Consultancy Granted",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return !(
                      values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancyRequestedOrRecommended == 1 &&
                      values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancySupportApproved == 1
                    );
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "specialistConsultancyRecommendationReason",
                  label: "Type of Specialist Consultancy and Reason for Recommendation",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancyRequestedOrRecommended != 2;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "specialistConsultancyRecommendedNotApprovedReason",
                  label: "Reason Support was not Approved",

                  warn: "Provide a reason that the additional support was not approved. This will be provided to the kindergarten.",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return !(
                      values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancyRequestedOrRecommended == 1 &&
                      values?.typeOfSupportRequested.isSpecialistConsultancy.specialistConsultancySupportApproved == 2
                    );
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonSpecialistConsultancyNotEligible",
                  label: "Reason Support was Not Approved",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistConsultancy?.specialistConsultancySupportApproved != 3;
                  },
                },
              ],
            },
            {
              type: "field",
              fieldType: "checkbox",
              key: "specialistTraining",
              label: "Specialist Training",
              props: {
                showDisabled: true,
              },
            },
            {
              type: "formGroup",
              key: "isSpecialistTraining",
              classNameOpt: "childComponent",
              hideExpression: (context, values) => {
                return !values?.typeOfSupportRequested.specialistTraining;
              },
              fields: [
                {
                  type: "field",
                  fieldType: "radio",
                  key: "specialistTrainingSupportApproved",
                  label: "Is Support Approved?",
                  props: {
                    isRequired: true,
                    options: [
                      {
                        label: "Yes",
                        value: 1,
                      },
                      {
                        label: "No - Application Eligible",
                        value: 2,
                      },
                      {
                        label: "No - Application Not Eligible",
                        value: 3,
                      },
                    ],
                  },
                  validation: Yup.number().required("A response is required"),
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "summaryOfSpecialistTrainingGranted",
                  label: "Summary of the Specialist Training Granted",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return !(values?.typeOfSupportRequested.isSpecialistTraining?.specialistTrainingSupportApproved == 1);
                  },
                },
                {
                  type: "formGroup",
                  key: "reasonSpecialistTrainingSupportNotApproved",
                  label: "Reason Support Was Not Approved",
                  props: {
                    isRequired: true,
                  },
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistTraining?.specialistTrainingSupportApproved != 2;
                  },
                  test: [
                    "chooseOne",
                    "A response is required",
                    (value: any) => {
                      return value.quoteNotProvided || value.notChildSpecificTraining || value.other;
                    },
                  ],
                  fields: [
                    {
                      type: "field",
                      fieldType: "checkbox",
                      key: "quoteNotProvided",
                      label: "Quote Not Provided",
                    },
                    {
                      type: "field",
                      fieldType: "checkbox",
                      key: "notChildSpecificTraining",
                      label: "Training Not Specific to the Child",
                    },
                    {
                      type: "field",
                      fieldType: "checkbox",
                      key: "other",
                      label: "Other",
                    },
                    {
                      type: "field",
                      fieldType: "textarea",
                      key: "otherReasonSpecialistTrainingNotApproved",
                      label: "Other (please specify)",
                      props: {
                        rows: 5,
                        col: 30,
                      },
                      validation: Yup.string().trim().required("A response is required"),
                      hideExpression: (context, values) => {
                        return !values?.typeOfSupportRequested.isSpecialistTraining.reasonSpecialistTrainingSupportNotApproved?.other;
                      },
                    },
                  ],
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "additionalInfoForSpecialistTrainingRequestDenial",
                  label: "Provide Additional Information as to Why the Request was Denied",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistTraining?.specialistTrainingSupportApproved != 2;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonSpecialistTrainingNotEligible",
                  label: "Reason Support was Not Approved",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isSpecialistTraining?.specialistTrainingSupportApproved != 3;
                  },
                },
              ],
            },
            {
              type: "field",
              fieldType: "checkbox",
              key: "minorWorks",
              label: "Minor Building Modifications",
              props: {
                showDisabled: true,
              },
            },
            {
              type: "formGroup",
              key: "isMinorWorks",
              classNameOpt: "childComponent",
              hideExpression: (context, values) => {
                return !values?.typeOfSupportRequested.minorWorks;
              },
              fields: [
                {
                  type: "field",
                  fieldType: "radio",
                  key: "minorWorksSupportApproved",
                  label: "Is Support Approved?",
                  props: {
                    isRequired: true,
                    options: [
                      {
                        label: "Yes",
                        value: 1,
                      },
                      {
                        label: "No - Application Eligible",
                        value: 2,
                      },
                      {
                        label: "No - Application Not Eligible",
                        value: 3,
                      },
                    ],
                  },
                  validation: Yup.number().required("A response is required"),
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "summaryMinorWorksGranted",
                  label: "Summary of the Minor Building Works Granted",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isMinorWorks?.minorWorksSupportApproved != 1;
                  },
                },
                {
                  type: "formGroup",
                  key: "reasonMinorWorksSupportNotApproved",
                  label: "Reason Support Was Not Approved",
                  props: {
                    isRequired: true,
                  },
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isMinorWorks?.minorWorksSupportApproved != 2;
                  },
                  test: [
                    "chooseOne",
                    "A response is required",
                    (value: any) => {
                      return value.quoteNotProvided || value.other;
                    },
                  ],
                  fields: [
                    {
                      type: "field",
                      fieldType: "checkbox",
                      key: "quoteNotProvided",
                      label: "Quote Not Provided",
                    },
                    {
                      type: "field",
                      fieldType: "checkbox",
                      key: "other",
                      label: "Other",
                    },
                    {
                      type: "field",
                      fieldType: "textarea",
                      key: "otherReasonMinorWorksSupportNotApproved",
                      label: "Other (please specify)",
                      warn: "This will not be provided to the kindergarten",
                      props: {
                        rows: 5,
                        col: 30,
                      },
                      validation: Yup.string().trim().required("A response is required"),
                      hideExpression: (context, values) => {
                        return !values?.typeOfSupportRequested.isMinorWorks.reasonMinorWorksSupportNotApproved?.other;
                      },
                    },
                  ],
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "additionalInfoForMinorWorksRequestDenial",
                  label: "Provide Additional Information as to Why the Request was Denied",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isMinorWorks?.minorWorksSupportApproved != 2;
                  },
                },
                {
                  type: "field",
                  fieldType: "textarea",
                  key: "reasonMinorWorksNotEligible",
                  label: "Reason Support was Not Approved",
                  warn: "This will be provided to the kindergarten",
                  props: {
                    rows: 5,
                    col: 30,
                  },
                  validation: Yup.string().trim().required("A response is required"),
                  hideExpression: (context, values) => {
                    return values?.typeOfSupportRequested.isMinorWorks?.minorWorksSupportApproved != 3;
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export function CalculateTotalHours(value1: number, value2: number): number {
  return Number(((value1 ?? 0) + (value2 ?? 0)).toFixed(1));
}
